/**
 * How big a hole is.
 *
 * Width used to be a constant. Every .pocketgolf hole was 20 tiles across, so the editor, the format
 * and the game all agreed on the number without any of them having to say it, and `GRID_SIZE_X` could
 * stand in for "the width of this hole" anywhere it was needed. A v2 hole now declares its own width by
 * how long its rows are — the same way it has always declared its height by how many rows it has — so
 * the number is read off the grid instead of assumed.
 *
 * That is why this module has no `width` field to set: the rows *are* the width. Resizing rewrites
 * them, and everything downstream measures what it finds. A stored width could disagree with the data,
 * and a hole whose declared width disagrees with its rows is the bug where the editor paints a column
 * the game will never read.
 *
 * ## The bounds
 *
 * `WIDTH_RANGE` and `HEIGHT_RANGE` are what the editor holds an author to. The game is more forgiving
 * than this — it crops a hole wider than the maximum rather than refusing the file, and plays a hole
 * narrower than the minimum as it finds it — because a parser's job is to make a file playable and an
 * editor's job is to not write one that needs rescuing.
 *
 * `DEFAULT_WIDTH` is different in kind from the other three: it is the width v1 froze, so it is both
 * the width a new hole starts at and the width the game plays *every* v1 hole at, whatever its rows
 * say. A v1 course must stay at it.
 *
 * The game's copy of these numbers is `Physics.gridWidth`, `Physics.gridWidthRange`,
 * `Physics.maxGridHeight` and `Physics.minGridHeight` in Sources/PocketGolfCore/Club.swift. The two
 * must agree; keep them in sync.
 */

/** The width every v1 hole is played at, and the width a new hole starts at. */
export const DEFAULT_WIDTH = 20;

/** The height a new hole starts at. */
export const DEFAULT_HEIGHT = 16;

/** What the editor will author, in tiles. Wider and taller than this is the game's problem, not ours. */
export const WIDTH_RANGE = { min: 15, max: 50 };
export const HEIGHT_RANGE = { min: 15, max: 150 };

/** Rough: what new ground is made of, in both directions. */
export const FILL_CHAR = 'R';

/**
 * How wide a hole is: the length of its rows.
 *
 * The first row rather than the longest, because everything that writes a grid here writes it square,
 * and a hole whose rows disagree is already broken in a way `courseWarnings` reports. (The game reads
 * the longest row, which is the forgiving reading of the same question — it has to cope with files no
 * editor wrote.)
 */
export function gridWidth(grid) {
	if (!Array.isArray(grid) || grid.length === 0) return 0;
	return typeof grid[0] === 'string' ? grid[0].length : 0;
}

/** A value forced into one of the ranges above. */
export function clampToRange(value, range) {
	if (!Number.isFinite(value)) return range.min;
	return Math.max(range.min, Math.min(range.max, Math.trunc(value)));
}

/** A grid of nothing but rough, at the given size. */
export function emptyGrid(width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT) {
	return Array.from({ length: height }, () => FILL_CHAR.repeat(width));
}

/**
 * The same hole at a new size.
 *
 * Grows with rough and crops from the right and the bottom — the corner an author is least likely to
 * have anchored a hole to, since a tee sits at the bottom *centre* and a green is somewhere up the
 * middle. Cropping is destructive and the caller is expected to have asked first.
 */
export function resizedGrid(grid, width, height, fill = FILL_CHAR) {
	const source = Array.isArray(grid) ? grid : [];
	return Array.from({ length: height }, (_, y) => {
		const row = typeof source[y] === 'string' ? source[y] : '';
		return row.length >= width ? row.slice(0, width) : row + fill.repeat(width - row.length);
	});
}
